import { type ColorParams, StrokeType } from '@bynder-studio/misc';
import { IStructuredGlyph } from '@bynder-studio/structured-text';
import { Position } from '../../Shared/Position';
import { BaseCompElement, BaseCompElementParams } from './BaseCompElement';
import { RadialGradientSize } from '../../../Helpers/utils';

export type SvgCompElementParams = BaseCompElementParams & {
    path: string;
    decorationPaths?: string[];
    horizontalPathScale: number;
    verticalPathScale: number;
    color: ColorParams;
    pathPosition: Position;
    strokeType: StrokeType;
    strokeWidth: number;
    fillColor?: ColorParams | null;
    strokeColor?: ColorParams | null;
    radialGradientSize: RadialGradientSize;
};

export class SvgCompElement extends BaseCompElement {
    path!: string;

    decorationPaths?: string[];

    horizontalPathScale = 1; // horizontal scaling to apply to SVG

    verticalPathScale = 1; // vertical scaling to apply to SVG

    color: ColorParams;

    // position to draw from (can be different from topleft)
    pathPosition!: Position;

    strokeType = StrokeType.NONE;

    strokeWidth = 0;

    /** Explicit fill color. When null, falls back to `color`. */
    fillColor: ColorParams | null = null;

    /** Explicit stroke/outline color. When null, falls back to `color`. */
    strokeColor: ColorParams | null = null;

    radialGradientSize: RadialGradientSize = 'outcircle';

    glyph?: IStructuredGlyph['data'];

    lineJoin?: CanvasLineJoin | null = null;

    lineCap?: CanvasLineCap | null = null;

    setProperties(params: SvgCompElementParams) {
        super.setProperties(params);
        this.path = params.path ?? this.path;
        this.decorationPaths = params.decorationPaths ?? this.decorationPaths;
        this.horizontalPathScale = params.horizontalPathScale ?? this.horizontalPathScale;
        this.verticalPathScale = params.verticalPathScale ?? this.verticalPathScale;
        this.color = params.color;
        this.pathPosition = params.pathPosition
            ? new Position(params.pathPosition.x, params.pathPosition.y)
            : this.pathPosition;
        this.strokeType = params.strokeType || StrokeType.NONE;
        this.strokeWidth = params.strokeWidth || 0;
        this.fillColor = params.fillColor ?? this.fillColor;
        this.strokeColor = params.strokeColor ?? this.strokeColor;
        this.radialGradientSize = params.radialGradientSize ?? this.radialGradientSize;
    }

    getLocalHashData() {
        return {
            ...super.getLocalHashData(),
            path: this.path,
            horizontalPathScale: this.horizontalPathScale,
            verticalPathScale: this.verticalPathScale,
            color: this.color,
            fillColor: this.fillColor,
            strokeColor: this.strokeColor,
            pathPosition: this.pathPosition.toObject(),
        };
    }

    toObject() {
        return {
            ...super.toObject(),
            type: 'SVG',
            path: this.path,
            decorationPaths: this.decorationPaths,
            horizontalPathScale: this.horizontalPathScale,
            verticalPathScale: this.verticalPathScale,
            color: this.color,
            pathPosition: this.pathPosition.toObject(),
            strokeType: this.strokeType,
            strokeWidth: this.strokeWidth,
            fillColor: this.fillColor,
            strokeColor: this.strokeColor,
            radialGradientSize: this.radialGradientSize,
            glyph: this.glyph,
            lineJoin: this.lineJoin,
            lineCap: this.lineCap,
        };
    }
}

export class SvgCompElementPool {
    private readonly pool: { [id: string]: SvgCompElement } = {};

    acquire(id: string): SvgCompElement {
        const pooledElement = this.pool[id];

        return pooledElement ?? new SvgCompElement();
    }

    releaseAll(elements: SvgCompElement[]): void {
        for (const el of elements) {
            el.transform = null;
            el.transforms = undefined;
            el.opacity = 1.0;
            el.rotation = 0.0;
            el.scale = 1.0;
            el.translatedX = 0;
            el.translatedY = 0;
            el.blur = 0.0;
            (el as any).remove = false;
            el.isInAnimation = false;
            this.pool[el.id] = el;
        }
    }

    clear(): void {
        Object.keys(this.pool).forEach((key) => delete this.pool[key]);
    }
}
